import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSystemRevenue } from '../../api';
import {
  PageHeader, TableSkeleton, Table, UserCell, AstrologerCell, StatusBadge,
} from '../../components/ui';
import { formatCurrency, formatDateTime } from '../../utils/format';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DollarSign, Landmark, RotateCcw, Wallet, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';

export default function RevenuePage() {
  const [days, setDays] = useState(30);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-revenue', days],
    queryFn: () => getSystemRevenue({ days }).then((r) => r.data.data),
    retry: 1,
  });

  const d = data;
  const byDay = (d?.byDay || []).map((r: any) => ({ date: r._id?.slice(5), amount: Math.round((r.total || 0) / 100) }));
  const byCat = (d?.byCategory || []).map((r: any) => ({
    name: `${r._id?.category || 'other'} (${r._id?.type || ''})`,
    amount: Math.round((r.total || 0) / 100),
  }));
  const recent = d?.recentTransactions || [];
  const payStatus = d?.paymentsByStatus || {};

  const cards = [
    { label: 'Wallet recharges', value: formatCurrency(d?.recharges?.total || 0), sub: `${d?.recharges?.count || 0} txns`, icon: <Wallet size={16} />, color: 'text-green-600' },
    { label: 'Tax / GST collected', value: formatCurrency(d?.taxCollected?.total || 0), sub: `${d?.taxCollected?.count || 0} paid payments`, icon: <Landmark size={16} />, color: 'text-amber-700' },
    { label: 'Gateway fees', value: formatCurrency(d?.gatewayFees?.total || 0), sub: 'Convenience fees on pay-in', icon: <Landmark size={16} />, color: 'text-violet-600' },
    { label: 'Bonus / cashback', value: formatCurrency(d?.bonuses?.total || 0), sub: `${d?.bonuses?.count || 0} credits`, icon: <ArrowUpCircle size={16} />, color: 'text-emerald-600' },
    { label: 'Consultation GMV', value: formatCurrency(d?.consultations?.gmv || 0), sub: `${d?.consultations?.count || 0} ended sessions`, icon: <DollarSign size={16} />, color: 'text-primary-600' },
    { label: 'Platform fee', value: formatCurrency(d?.consultations?.platformFee || 0), sub: 'From ended consultations', icon: <Landmark size={16} />, color: 'text-violet-600' },
    { label: 'Astrologer earnings', value: formatCurrency(d?.consultations?.astrologerEarnings || 0), sub: 'From ended consultations', icon: <DollarSign size={16} />, color: 'text-amber-600' },
    { label: 'Refunds', value: formatCurrency(d?.refunds?.total || 0), sub: `${d?.refunds?.count || 0} refunds`, icon: <RotateCcw size={16} />, color: 'text-red-600' },
    { label: 'Withdrawals paid', value: formatCurrency(d?.payouts?.total || 0), sub: `${d?.payouts?.count || 0} withdrawals`, icon: <Landmark size={16} />, color: 'text-gray-700' },
    { label: 'Net platform revenue', value: formatCurrency(d?.netPlatformRevenue || 0), sub: 'Fees + tax − refunds', icon: <DollarSign size={16} />, color: 'text-emerald-700' },
  ];

  return (
    <div className="p-6">
      <PageHeader title="System revenue" subtitle="Recharges, consultation GMV, platform fee and tracked activity">
        <div className="flex gap-2">
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              onClick={() => setDays(n)}
              className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${days === n ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600'}`}
            >
              Last {n}d
            </button>
          ))}
          <Link to="/finance/transactions" className="btn-secondary btn-sm">Transactions</Link>
          <Link to="/finance/payments" className="btn-secondary btn-sm">Payments</Link>
        </div>
      </PageHeader>

      {isError ? (
        <div className="card p-6 mb-5 text-center">
          <p className="text-sm text-red-600 font-medium">Could not load system revenue</p>
          <p className="text-xs text-gray-500 mt-1">{(error as any)?.response?.data?.message || (error as Error)?.message || 'Request failed'}</p>
          <button type="button" className="btn-primary btn-sm mt-3" onClick={() => refetch()}>Retry</button>
        </div>
      ) : null}

      {isLoading ? (
        <TableSkeleton rows={4} cols={4} />
      ) : null}

      {!isLoading && !isError ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            {cards.map((c) => (
              <div key={c.label} className="card p-4">
                <div className={`mb-2 ${c.color}`}>{c.icon}</div>
                <p className="text-xs text-gray-500">{c.label}</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{c.value}</p>
                <p className="text-[11px] text-gray-400 mt-1">{c.sub}</p>
              </div>
            ))}
          </div>

          {Object.keys(payStatus).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {Object.entries(payStatus).map(([status, v]: [string, any]) => (
                <div key={status} className="rounded-lg border border-gray-100 px-3 py-2 text-xs">
                  <span className="capitalize font-medium text-gray-700">{status}</span>
                  <span className="text-gray-400 mx-1.5">·</span>
                  <span className="font-semibold">{formatCurrency(v.total || 0)}</span>
                  <span className="text-gray-400 ml-1">({v.count})</span>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <div className="card p-5">
              <h3 className="font-semibold mb-4">Daily recharges (₹)</h3>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={byDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v: any) => [`₹${v}`, 'Amount']} />
                  <Area type="monotone" dataKey="amount" stroke="#F5811F" fill="#F5811F22" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="card p-5">
              <h3 className="font-semibold mb-4">By category</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={byCat.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v: any) => [`₹${v}`, 'Amount']} />
                  <Bar dataKey="amount" fill="#FF4500" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-sm">Recent transactions</h3>
                <p className="text-[11px] text-gray-400">Click party name to open profile · last 15 in range</p>
              </div>
              <Link to="/finance/transactions" className="text-xs text-primary-600 hover:underline">View all</Link>
            </div>
            {recent.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-400">No transactions in this period</div>
            ) : (
              <Table>
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="table-th">Party</th>
                    <th className="table-th">Type</th>
                    <th className="table-th">Category</th>
                    <th className="table-th">Description</th>
                    <th className="table-th">Amount</th>
                    <th className="table-th">Status</th>
                    <th className="table-th">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((t: any) => (
                    <tr key={t._id} className="table-row">
                      <td className="table-td">
                        {t.userType === 'astrologer' ? (
                          <AstrologerCell entity={t.party || { _id: t.userId }} />
                        ) : (
                          <UserCell entity={t.party || { _id: t.userId }} />
                        )}
                      </td>
                      <td className="table-td">
                        <div className={`flex items-center gap-1 text-xs font-semibold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {t.type === 'credit' ? <ArrowUpCircle size={12} /> : <ArrowDownCircle size={12} />}
                          {t.type}
                        </div>
                      </td>
                      <td className="table-td text-xs capitalize">{t.category?.replace(/_/g, ' ')}</td>
                      <td className="table-td text-xs max-w-[200px] truncate" title={t.description}>{t.description}</td>
                      <td className="table-td">
                        <span className={`text-xs font-bold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {t.type === 'credit' ? '+' : '-'}{formatCurrency(t.amount)}
                        </span>
                      </td>
                      <td className="table-td"><StatusBadge status={t.status || 'completed'} /></td>
                      <td className="table-td text-xs whitespace-nowrap">{formatDateTime(t.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
